import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Box, Button, InputAdornment, TextField, Typography, styled, Checkbox, FormControlLabel } from '@mui/material';

import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { LoadCanvasTemplateNoReload, loadCaptchaEnginge, validateCaptcha } from 'react-simple-captcha';
import MyDialog from '../../../../components/dialog-component/my-dialog';
import Iconify from '../../../../components/Iconify';
import { dispatch } from '../../../../redux/store';
import { openModal } from '../../../../redux/slices/authUiSlice';

import useAuth from '../../../../hooks/useAuth';
import useLocales from '../../../../hooks/useLocales';

// ---------------- styled ----------------

const StyledTextField = styled(TextField)({
  marginBottom: '16px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#e0e0e0',
      borderWidth: '2px',
    },

    '&:hover fieldset': {
      borderColor: '#e0e0e0',
    },

    '&.Mui-focused fieldset': {
      borderColor: '#f0a500',
      borderWidth: '2px',
    },

    '& input': {
      color: '#000',
    },

    '& input::placeholder': {
      color: '#bbb',
      opacity: 1,
    },
  },
});

const GradientButton = styled(Button)(() => ({
  padding: '12px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  background: 'linear-gradient(180deg, #f5d08a, #e0a95c)',
  color: '#333',

  '&:hover': {
    background: 'linear-gradient(180deg, #eac47a, #d89b4e)',
  },

  '&.Mui-disabled': {
    opacity: 0.6,
    color: '#333',
    background: 'linear-gradient(180deg, #f5d08a, #e0a95c)',
  },
}));

const InputField = ({ icon, endIcon, onEndClick, ...props }) => (
  <StyledTextField
    fullWidth
    {...props}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Iconify icon={icon} width={22} height={22} color="#bbb" />
        </InputAdornment>
      ),
      endAdornment: endIcon && (
        <InputAdornment position="end" onClick={onEndClick} sx={{ cursor: 'pointer' }}>
          <Iconify icon={endIcon} width={22} height={22} color="#bbb" />
        </InputAdornment>
      ),
    }}
  />
);

InputField.propTypes = {
  endIcon: PropTypes.any,
  icon: PropTypes.any,
  onEndClick: PropTypes.func,
};

// ---------------- component ----------------

const Register = ({ handleClose, handleOpen, authData }) => {
  const { register } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const navigate = useNavigate();

  const [registerForm, setRegisterForm] = useState({
    userCountryCode: '+880',
    userUserName: '',
    userPassword: '',
    userPhoneNumber: '',
    userFullName: '',
    userReferralCode: '',
    otpCode: '',
    captcha: '',
  });

  const [errors] = useState({});
  const [agree, setAgree] = useState(true);
  const [showReferral, setShowReferral] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState({});

  const handleChange = (key, value) => {
    setRegisterForm((prev) => ({ ...prev, [key]: value }));
  };

  const cleanedPhone = registerForm.userPhoneNumber.replace(/^0/, '');
  const fullPhone = `${registerForm.userCountryCode}${cleanedPhone}`;

  // ---------------- validation (OLD LOGIC) ----------------
  const isValid = () => {
    let valid = true;
    const _error = {};

    // username
    if (registerForm?.userUserName?.trim()?.length < 3 && !authData) {
      valid = false;
      _error.userUserName = translate('invalid_x_must_be_atleast_y_characters', { x: translate('username'), y: '3' });
    } else {
      _error.userUserName = '';
    }

    /**
     * Check if it is a valid Nepal number
     *   */
    const validatePhoneNumber = () => {
      // Check first 4 digits(Network provider)
      // check length as well
      // if (validMobileNetworks(startsWith) && phoneNumber.length <= maxLength && phoneNumber.length >= minLength) {
      //   _error.userPhoneNumber = '';
      // } else {
      //   valid = false;
      //   _error.userPhoneNumber = translate('must_start_with');
      // }
    };

    if (registerForm?.userPhoneNumber.trim()) {
      validatePhoneNumber(registerForm?.userPhoneNumber.trim());
      // validateNumber();
    } else {
      valid = false;
      _error.userPhoneNumber = translate('must_start_with');
    }

    // password
    if (registerForm?.userPassword?.trim()?.length < 5 && !authData) {
      valid = false;
      _error.userPassword = translate('invalid_x_must_be_atleast_y_characters', { x: translate('password'), y: '5' });
    } else {
      _error.userPassword = '';
    }

    // fullname
    if (!registerForm?.userFullName.trim()) {
      valid = false;
      _error.userFullName = translate('invalid_x_must_be_atleast_y_characters', { x: translate('full_name'), y: '1' });
    } else {
      _error.userFullName = '';
    }

    // captcha validation
    if (validateCaptcha(registerForm.captcha) === true) {
      _error.userCaptcha = '';
    } else {
      valid = false;
      _error.userCaptcha = translate('captcha_not_match');
      handleChange('captcha', '');
    }

    setFormError(() => _error);

    return valid;
  };

  // ---------------- SUBMIT ----------------
  const onSubmit = async () => {
    if (!isValid()) return;

    const requestData = {
      // 'user-username': registerForm?.userPhoneNumber?.replace(/\D/g, ''),
      'user-username': registerForm?.userUserName.trim(),
      'user-password': registerForm?.userPassword.trim(),
      'user_profile-no_hp': fullPhone,
      'user-full_name': registerForm?.userFullName,
      // 'user-email': registerForm?.userEmail,
      // 'user_profile-birthday': moment(registerForm?.userBirthday).format('YYYY-MM-DD'),
      'user-referral_code': registerForm?.userReferralCode,
    };

    if (authData) {
      const _authData = JSON.parse(JSON.stringify(authData));
      delete _authData?.platform;
      requestData['user_third_party_login-platform'] = authData?.platform;

      requestData['user_third_party_login-data'] = JSON.stringify(_authData);
    }

    try {
      setIsLoading(true);
      console.log(requestData);
      await register(requestData);
      const _referralCode = localStorage.getItem(`referralCode`);
      if (_referralCode) localStorage.removeItem(`referralCode`);
      // dispatch(openLoginSignup({ open: false, isLogin: true }));
      navigate('/');
    } catch (err) {
      console.log(`Error: ${err.message}`);
      const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
      enqueueSnackbar(err.message, snackbarOptions);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!handleOpen) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          loadCaptchaEnginge(4, '#ffffff', '#000000', 'numbers');
        } catch (err) {
          console.log('Captcha error:', err);
        }
      }, 100);
    });
  }, [handleOpen]);

  useEffect(() => {
    const referralCode = localStorage.getItem('referralCode');

    if (referralCode) {
      setRegisterForm((prev) => ({
        ...prev,
        userReferralCode: referralCode,
      }));

      setShowReferral(true);
    }
  }, []);

  const isDisabled =
    !registerForm.userUserName ||
    !registerForm.userPassword ||
    !registerForm.userPhoneNumber ||
    !registerForm.userFullName ||
    !agree;

  // ---------------- UI ----------------
  return (
    <MyDialog open={handleOpen} close={handleClose} maxSize="sm" title={translate('create_your_account')}>
      {/* Phone */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Country Code Select */}
        <Box
          sx={{
            width: 130,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            border: '1px solid #ccc',
            borderRadius: '10px',
            backgroundColor: '#f5f5f5',
            color: '#000',
          }}
        >
          <img
            src="https://flagcdn.com/w40/bd.png"
            alt="BD flag"
            style={{
              width: 20,
              height: 14,
              objectFit: 'cover',
              borderRadius: '2px',
            }}
          />
          <span>{registerForm.userCountryCode}</span>
        </Box>

        {/* Phone Number Input */}
        <InputField
          fullWidth
          placeholder="0XXXXXXXXX"
          value={registerForm.userPhoneNumber}
          onChange={(e) => handleChange('userPhoneNumber', e.target.value)}
        />
      </Box>
      {/* Username */}
      <InputField
        placeholder={translate('username')}
        value={registerForm.userUserName}
        onChange={(e) => handleChange('userUserName', e.target.value)}
        error={!!errors.username}
        helperText={errors.username}
      />
      {/* Full Name */}
      <InputField
        placeholder={translate('full_name')}
        value={registerForm.userFullName}
        onChange={(e) => handleChange('userFullName', e.target.value)}
        error={!!errors.fullName}
        helperText={errors.fullName}
      />
      {/* Password */}
      <InputField
        placeholder={translate('password')}
        type={showPassword ? 'text' : 'password'}
        value={registerForm.userPassword}
        onChange={(e) => handleChange('userPassword', e.target.value)}
        endIcon={showPassword ? 'mdi:eye-outline' : 'mdi:eye-off-outline'}
        onEndClick={() => setShowPassword(!showPassword)}
        error={!!errors.password}
        helperText={errors.password}
      />
      <Box
        display="flex"
        width="100%"
        gap={1}
        alignItems="flex-start"
        sx={{
          '& canvas': {
            height: '42px !important',
            width: '120px !important',
            borderRadius: '8px',
            cursor: 'pointer',
            border: '1px solid #ccc',
            display: 'block',
            boxSizing: 'border-box',
          },
          '& #react-captcha-load-canvas-no-reload': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '56px',
          },
          '& .MuiFormHelperText-root': {
            position: 'absolute',
            bottom: '-20px',
          },
        }}
      >
        {handleOpen && <LoadCanvasTemplateNoReload />}

        <InputField
          placeholder={translate('enter_text_on_the_left')}
          value={registerForm.captcha}
          onChange={(e) => handleChange('captcha', e.target.value)}
          error={!!formError.userCaptcha}
          helperText={formError.userCaptcha}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '42px',
            },
          }}
        />
      </Box>

      {/* Referral Toggle */}
      <Box
        onClick={() => setShowReferral(!showReferral)}
        sx={{
          border: '2px solid #e0e0e0',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          borderBottomLeftRadius: showReferral ? '0' : '8px',
          borderBottomRightRadius: showReferral ? '0' : '8px',
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          maxWidth: '210px',
        }}
      >
        <Typography fontSize={'14px'} color="#666">
          {translate('referral_affilate')}
        </Typography>

        <Iconify icon={showReferral ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={22} height={22} color="#666" />
      </Box>

      {showReferral && (
        <Box
          sx={{
            border: '2px solid #e0e0e0',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            p: 1,
            mb: 2,
          }}
        >
          <InputField
            sx={{ mb: 0 }}
            placeholder={translate('please_enter_code')}
            icon="mdi:account-group-outline"
            value={registerForm.userReferralCode}
            onChange={(e) => handleChange('userReferralCode', e.target.value)}
          />
        </Box>
      )}

      {/* Terms */}
      <FormControlLabel
        control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
        label={translate('terms_and_services')}
        sx={{ color: 'black', mt: 2 }}
      />
      {/* Button */}
      <GradientButton fullWidth disabled={isDisabled || isLoading} onClick={onSubmit} sx={{ mt: 2 }}>
        {isLoading ? 'Registering...' : translate('confirm')}
      </GradientButton>
      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 2, color: '#000000' }}>
        <Typography>
          {translate('already_have_account')}
          <Typography
            style={{ color: '#4caf50', cursor: 'pointer' }}
            onClick={() => dispatch(openModal({ view: 'login' }))}
          >
            {translate('login')}
          </Typography>
        </Typography>
      </Box>
    </MyDialog>
  );
};

Register.propTypes = {
  authData: PropTypes.shape({
    platform: PropTypes.any,
  }),
  handleClose: PropTypes.func,
  handleOpen: PropTypes.func,
};

export default Register;
